import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Building2, LogOut, RefreshCw, Loader2, Sparkles, Radio } from 'lucide-react';
import { Button } from '../components/ui/button';
import OwnerOutletCard from '../components/OwnerOutletCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OwnerDashboardPage = () => {
  const { user, logout } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (silent) => {
    if (silent) setRefreshing(true);
    try {
      const r = await axios.get(`${API}/owner/outlets`);
      setData(r.data);
    } catch { /* keep last good */ }
    finally { setLoading(false); setRefreshing(false); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(() => load(true), 20000); // live refresh every 20s
    return () => clearInterval(t);
  }, [load]);

  const outlets = data?.outlets || [];
  const aiCount = outlets.filter((o) => o.client_type === 'ai').length;
  const liveCount = outlets.filter((o) => o.online).length;

  return (
    <div className="min-h-screen bg-[#060606] text-white" data-testid="owner-dashboard">
      {/* Header */}
      <div className="border-b border-white/10 bg-black/40 backdrop-blur-xl sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#00FFFF] to-[#FF007F] flex items-center justify-center">
              <Building2 className="w-5 h-5 text-black" />
            </div>
            <div>
              <h1 className="text-lg font-extrabold tracking-tight" style={{ fontFamily: 'Outfit' }}>Owner Control Center</h1>
              <p className="text-[11px] text-gray-500">{data?.owner?.name || user?.name} · {outlets.length} outlet{outlets.length === 1 ? '' : 's'}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => load(true)} className="h-9 px-3 rounded-full text-xs text-gray-300 hover:bg-white/10" data-testid="owner-refresh">
              {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              <span className="hidden sm:inline ml-1.5">Refresh</span>
            </Button>
            <Button variant="ghost" onClick={logout} className="h-9 px-3 rounded-full text-xs text-gray-300 hover:bg-white/10" data-testid="owner-logout">
              <LogOut className="w-4 h-4" /><span className="hidden sm:inline ml-1.5">Sign out</span>
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-5 lg:px-8 py-8">
        {/* Summary strip */}
        {!loading && outlets.length > 0 && (
          <div className="flex flex-wrap gap-3 mb-8" data-testid="owner-summary">
            <div className="flex items-center gap-2 bg-white/[0.04] border border-white/10 rounded-full px-4 py-2 text-sm">
              <span className="w-2 h-2 rounded-full bg-[#39FF14] animate-pulse" /> {liveCount} live now
            </div>
            <div className="flex items-center gap-2 bg-white/[0.04] border border-white/10 rounded-full px-4 py-2 text-sm">
              <Sparkles className="w-3.5 h-3.5 text-[#00FFFF]" /> {aiCount} AI
            </div>
            <div className="flex items-center gap-2 bg-white/[0.04] border border-white/10 rounded-full px-4 py-2 text-sm">
              <Radio className="w-3.5 h-3.5 text-[#FF007F]" /> {outlets.length - aiCount} commercial
            </div>
          </div>
        )}

        {loading ? (
          <div className="py-24 flex justify-center"><Loader2 className="w-6 h-6 text-gray-500 animate-spin" /></div>
        ) : outlets.length === 0 ? (
          <div className="py-24 text-center" data-testid="owner-empty">
            <Building2 className="w-10 h-10 text-gray-700 mx-auto mb-3" />
            <p className="text-gray-400">No outlets assigned yet.</p>
            <p className="text-gray-600 text-sm mt-1">Ask your admin to assign your venues to this owner account.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="owner-outlets-grid">
            {outlets.map((o) => (
              <OwnerOutletCard key={o.id} outlet={o} onChanged={() => load(true)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default OwnerDashboardPage;
